import { useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { motion, useReducedMotion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

interface BrochureDownloadProps {
  pdfType: 'monosign' | 'monopam' | 'monosync'
  productName: string
  copy: string
  previewImage?: { src: string; alt: string }
}

type ResponseType = 'success' | 'failure' | 'none'

const labels = {
  en: {
    eyebrow: 'Product brief',
    title: (name: string) => `Want the deeper dive on ${name}?`,
    name: 'Name',
    email: 'Email',
    required: '(required)',
    namePlaceholder: 'Jane Doe',
    submit: 'Download the brief \u2192',
    sending: 'Sending\u2026',
    success: 'Thanks for submitting! Check your email for the product brief.',
    failure:
      'Something went wrong. You can mail <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> instead.',
  },
  tr: {
    eyebrow: '\u00dcr\u00fcn dok\u00fcman\u0131',
    title: (name: string) => `${name} hakk\u0131nda derinlemesine bilgi ister misiniz?`,
    name: 'Ad Soyad',
    email: 'E-posta',
    required: '(zorunlu)',
    namePlaceholder: 'Ad Soyad',
    submit: 'Dok\u00fcman\u0131 indir \u2192',
    sending: 'G\u00f6nderiliyor\u2026',
    success: 'Te\u015fekk\u00fcrler! \u00dcr\u00fcn dok\u00fcman\u0131 i\u00e7in e-postan\u0131z\u0131 kontrol edin.',
    failure:
      'Bir \u015feyler ters gitti. Dilerseniz <a href="mailto:sales@monofor.com" class="underline">sales@monofor.com</a> adresine yazabilirsiniz.',
  },
}

export default function BrochureDownload({
  pdfType,
  productName,
  copy,
  previewImage,
}: BrochureDownloadProps) {
  const { locale } = useRouter()
  const reducedMotion = useReducedMotion()
  const t = locale === 'tr' ? labels.tr : labels.en
  const nameField = useRef<HTMLInputElement>(null)
  const emailField = useRef<HTMLInputElement>(null)
  const [responseType, setResponseType] = useState<ResponseType>('none')
  const [responseMessage, setResponseMessage] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isSubmitting) return
    setIsSubmitting(true)

    const data = {
      name: nameField.current?.value ?? '',
      email: emailField.current?.value ?? '',
      pdfType,
    }

    try {
      const res = await fetch('/api/subscribe_pdf', {
        method: 'POST',
        headers: {
          Accept: 'application/json, text/plain, */*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      if (res.status === 200) {
        setResponseType('success')
        setResponseMessage(t.success)
        if (nameField.current) nameField.current.value = ''
        if (emailField.current) emailField.current.value = ''
      } else {
        setResponseType('failure')
        setResponseMessage(t.failure)
      }
    } catch (err) {
      setResponseType('failure')
      setResponseMessage(t.failure)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Section tone="brand" density="spacious" id="brochure">
      <Container size="xl">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
          <div>
            <Eyebrow>{t.eyebrow}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {t.title(productName)}
            </Heading>
            <Text size="xl" tone="secondary" className="mt-6">
              {copy}
            </Text>
          </div>

          <motion.form
            initial={reducedMotion ? false : { opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onSubmit={handleSubmit}
            className="rounded-3xl border border-line-subtle bg-surface p-8 shadow-card lg:p-10"
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="brochure-name"
                  className="block text-body-sm font-medium text-ink-primary"
                >
                  {t.name} <span className="text-ink-tertiary">{t.required}</span>
                </label>
                <input
                  id="brochure-name"
                  ref={nameField}
                  type="text"
                  name="name"
                  required
                  minLength={2}
                  placeholder={t.namePlaceholder}
                  autoComplete="name"
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              <div>
                <label
                  htmlFor="brochure-email"
                  className="block text-body-sm font-medium text-ink-primary"
                >
                  {t.email} <span className="text-ink-tertiary">{t.required}</span>
                </label>
                <input
                  id="brochure-email"
                  ref={emailField}
                  type="email"
                  name="email"
                  required
                  placeholder="you@your-company.com"
                  autoComplete="email"
                  className="mt-2 w-full rounded-xl border border-line bg-surface px-4 py-3 text-body-md text-ink-primary placeholder:text-ink-muted focus:border-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                />
              </div>
              {responseMessage && (
                <div
                  className={`rounded-xl px-4 py-3 text-body-sm ${
                    responseType === 'success'
                      ? 'bg-accent-50 text-accent-800'
                      : 'bg-[#fef2f2] text-[#b91c1c]'
                  }`}
                  dangerouslySetInnerHTML={{ __html: responseMessage }}
                />
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex w-full items-center justify-center rounded-pill bg-brand-900 px-7 py-3.5 text-body-md font-medium text-white transition hover:bg-brand-800 disabled:opacity-50"
              >
                {isSubmitting ? t.sending : t.submit}
              </button>
            </div>
          </motion.form>
        </div>
      </Container>
    </Section>
  )
}
